import { groupBy } from 'lodash';

import { BatchLTLReplySummary } from 'types/BatchLTLReply';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestedLoadChange } from 'types/suggestions/LoadSuggestions';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import { getSuggestionTitleByPipeline } from 'utils/suggestions/getSuggestionTitleByPipeline';
import { formatLoadInstantForDisplay } from 'utils/time';

export type AppointmentDisplayTimezones = {
  pickup?: MaybeUndef<string>;
  dropoff?: MaybeUndef<string>;
};

export type SuggestionDescriptorContext = {
  tmsName?: Maybe<string>;
  appointmentTimezones?: AppointmentDisplayTimezones;
  // Adds the destination to a load building title that other cards share (e.g. one card per load of
  // a daily load list, all for the same customer).
  showLoadBuildingDestination?: boolean;
  // Batch LTL shipments that all ship from one place show only where each one goes.
  showQuickQuoteDestinationOnly?: boolean;
};

// Ordinal for Kit suggestion pills (e.g. "1."), no pipeline acronyms.
export function getPillLabel(
  pipeline: SuggestionPipelines,
  index: number,
  batchIndex?: number
): string {
  if (pipeline === SuggestionPipelines.QuickQuote && batchIndex !== undefined) {
    return `${batchIndex + 1}.`;
  }
  return `${index + 1}.`;
}

export function getSuggestionDescriptor(
  suggestion: GenericSuggestion,
  context?: SuggestionDescriptorContext
): string {
  if (suggestion.pipeline === SuggestionPipelines.QuickQuote) {
    const sug = (suggestion as SuggestedQuoteChange).suggested;
    if (!sug) {
      return getSuggestionTitleByPipeline(suggestion.pipeline);
    }
    const pickup =
      [sug.pickupCity, sug.pickupState].filter(Boolean).join(', ') || null;
    const delivery =
      [sug.deliveryCity, sug.deliveryState].filter(Boolean).join(', ') || null;
    if (context?.showQuickQuoteDestinationOnly) {
      const destination = [delivery, sug.deliveryZip].filter(Boolean).join(' ');
      return `→ ${destination || '...'}`;
    }
    if (pickup && delivery) {
      return `${pickup} to ${delivery}`;
    }
    if (pickup) {
      return `Pickup in ${pickup}`;
    }
    if (delivery) {
      return `Dropoff in ${delivery}`;
    }
    return '...';
  }
  if (suggestion.pipeline === SuggestionPipelines.LoadBuilding) {
    const suggested = (suggestion as LoadBuildingSuggestions).suggested;
    const name = suggested?.customer?.name?.trim();
    const pickupLoc =
      suggested?.pickup?.city || suggested?.pickup?.state
        ? [suggested.pickup.city, suggested.pickup.state]
            .filter(Boolean)
            .join(', ')
        : null;
    const dropoffLoc =
      suggested?.consignee?.city || suggested?.consignee?.state
        ? [suggested.consignee.city, suggested.consignee.state]
            .filter(Boolean)
            .join(', ')
        : null;

    let primary: Maybe<string> = null;
    if (name) {
      primary = name;
    } else if (pickupLoc) primary = `Pickup in ${pickupLoc}`;
    else if (dropoffLoc) primary = `Dropoff in ${dropoffLoc}`;

    const refOrPo =
      suggested?.customer?.refNumber?.trim() || suggested?.poNums?.trim() || '';

    const destination =
      dropoffLoc || suggested?.consignee?.name?.trim() || null;
    if (
      context?.showLoadBuildingDestination &&
      name &&
      destination &&
      primary === name
    ) {
      // Destination first: long customer names truncate, and the destination is what differs.
      primary = `${destination} · ${name}`;
    }

    if (primary && refOrPo) {
      return `${primary} (${refOrPo})`;
    }
    if (primary) {
      return primary;
    }
    if (refOrPo) {
      return refOrPo;
    }
    return '...';
  }
  if (suggestion.pipeline === SuggestionPipelines.CarrierInfo) {
    const sug = (suggestion as SuggestedLoadChange).suggested as Undef<{
      firstDriverName?: string;
      truckNumber?: string;
      trailerNumber?: string;
      dispatchCity?: string;
      dispatchState?: string;
    }>;
    const driver = sug?.firstDriverName?.trim();
    const truck = sug?.truckNumber?.trim();
    const trailer = sug?.trailerNumber?.trim();
    const loc =
      [sug?.dispatchCity, sug?.dispatchState].filter(Boolean).join(', ') ||
      null;
    const parts = [driver, truck, trailer, loc].filter(Boolean);
    if (parts.length > 0) {
      return parts.join(' - ');
    }
    return getSuggestionTitleByPipeline(suggestion.pipeline);
  }
  if (suggestion.pipeline === SuggestionPipelines.ApptConfirmation) {
    const sug = (suggestion as SuggestedLoadChange).suggested as Undef<{
      pickupApptTime?: Date;
      dropoffApptTime?: Date;
    }>;
    const pickup = sug?.pickupApptTime;
    const dropoff = sug?.dropoffApptTime;
    const formatAppt = (
      value: Date | string | undefined,
      stopTimezone: MaybeUndef<string>,
      format: string
    ) =>
      formatLoadInstantForDisplay(value, {
        tmsName: context?.tmsName,
        stopTimezone,
        format,
      });

    const pickupDay = pickup
      ? formatAppt(pickup, context?.appointmentTimezones?.pickup, 'MMM D')
      : '';
    const dropoffDay = dropoff
      ? formatAppt(dropoff, context?.appointmentTimezones?.dropoff, 'MMM D')
      : '';
    const pickupSingle = pickup
      ? formatAppt(
          pickup,
          context?.appointmentTimezones?.pickup,
          'MMM D, HH:mm'
        )
      : '';
    const dropoffSingle = dropoff
      ? formatAppt(
          dropoff,
          context?.appointmentTimezones?.dropoff,
          'MMM D, HH:mm'
        )
      : '';

    if (pickupDay && dropoffDay) {
      return `${pickupDay} -> ${dropoffDay}`;
    }
    if (pickupSingle) {
      return pickupSingle;
    }
    if (dropoffSingle) {
      return dropoffSingle;
    }
    return getSuggestionTitleByPipeline(suggestion.pipeline);
  }
  if (suggestion.pipeline === SuggestionPipelines.CheckCall) {
    const suggested = (suggestion as SuggestedLoadChange).suggested as Undef<{
      checkCallChanges?: {
        status?: string;
        city?: string;
        state?: string;
      };
    }>;
    const cc = suggested?.checkCallChanges;
    const status = cc?.status?.trim() || null;
    const city = cc?.city?.trim();
    const state = cc?.state?.trim();
    const loc = [city, state].filter(Boolean).join(', ') || null;
    if (status && loc) {
      return `${status} - ${loc}`;
    }
    if (status) {
      return status;
    }
    if (loc) {
      return loc;
    }
    return getSuggestionTitleByPipeline(suggestion.pipeline);
  }
  return getSuggestionTitleByPipeline(suggestion.pipeline);
}

export type SuggestionTitleParts = {
  // List ordinal including trailing period, e.g. "1."
  ordinal: string;
  // Human-readable body without the ordinal
  descriptor: string;
  // Combined title for aria-labels / native tooltips, e.g. "1. Acme Logistics"
  fullTitle: string;
};

// Load building suggestions whose title another card shares, so each can show its destination.
export function getRepeatedLoadBuildingIds(
  suggestions: GenericSuggestion[],
  context?: SuggestionDescriptorContext
): Set<number> {
  const byTitle = groupBy(
    suggestions.filter((s) => s.pipeline === SuggestionPipelines.LoadBuilding),
    (s) => getSuggestionDescriptor(s, context)
  );
  return new Set(
    Object.values(byTitle)
      .filter((group) => group.length > 1)
      .flat()
      .map((s) => s.id)
  );
}

export function getSuggestionTitleParts(
  suggestion: GenericSuggestion,
  pipeline: SuggestionPipelines,
  index: number,
  batchIndex?: number,
  context?: SuggestionDescriptorContext
): SuggestionTitleParts {
  const ordinal = getPillLabel(pipeline, index, batchIndex);
  const descriptor =
    pipeline === SuggestionPipelines.QuickQuote && batchIndex !== undefined
      ? 'Batch Quote'
      : getSuggestionDescriptor(suggestion, context);
  return {
    ordinal,
    descriptor,
    fullTitle: `${ordinal} ${descriptor}`,
  };
}

export function getSuggestionTitle(
  suggestion: GenericSuggestion,
  pipeline: SuggestionPipelines,
  index: number,
  batchIndex?: number,
  context?: SuggestionDescriptorContext
): string {
  return getSuggestionTitleParts(
    suggestion,
    pipeline,
    index,
    batchIndex,
    context
  ).fullTitle;
}

// Group Quick Quote suggestions by email so each email's quotes form one batch. Batch
// LTL shipments follow the shipper's order in the email when it is known.
export function getQuickQuoteBatches<T extends GenericSuggestion>(
  suggestions: T[]
): T[][] {
  const byEmail = groupBy(suggestions, (s) => s.emailID);
  return Object.values(byEmail).map((batch) =>
    batch
      .map((suggestion, position) => ({
        suggestion,
        order: getBatchLTLShipmentIndex(suggestion) ?? position + 1,
      }))
      .sort((a, b) => a.order - b.order)
      .map(({ suggestion }) => suggestion)
  );
}

// The Batch LTL reply summary when it belongs to an email in this suggestion list.
export function getActiveBatchLTLReplySummary(
  summary: Maybe<BatchLTLReplySummary>,
  suggestions: GenericSuggestion[]
): Maybe<BatchLTLReplySummary> {
  if (!summary) {
    return null;
  }

  const isForListedEmail = suggestions.some(
    (suggestion) =>
      suggestion.pipeline === SuggestionPipelines.QuickQuote &&
      suggestion.emailID === summary.emailId
  );

  return isForListedEmail ? summary : null;
}

// Batch LTL rows show only destinations when the batch shares an origin, including the
// shipments left after a reply.
export function shouldShowBatchLTLDestinationOnly(
  batch: GenericSuggestion[],
  replySummary: Maybe<BatchLTLReplySummary>
): boolean {
  if (isBatchLTLSharedOrigin(batch)) {
    return true;
  }

  return Boolean(
    replySummary?.hasSharedOrigin &&
    batch.length > 0 &&
    isBatchLTLBatch(batch) &&
    batch.every((suggestion) => suggestion.emailID === replySummary.emailId)
  );
}

// True for a batch of LTL shipments that all ship from the same place.
export function isBatchLTLSharedOrigin(batch: GenericSuggestion[]): boolean {
  if (batch.length < 2 || !isBatchLTLBatch(batch)) {
    return false;
  }

  const origins = new Set(
    batch.map((suggestion) => {
      const sug = (suggestion as SuggestedQuoteChange).suggested;
      return (
        sug?.pickupZip?.trim() ||
        [sug?.pickupCity, sug?.pickupState].filter(Boolean).join(', ')
      );
    })
  );

  return origins.size === 1 && !origins.has('');
}

// "6 LTL shipments" when every suggestion in the batch is a Batch LTL shipment.
export function getQuickQuoteBatchLabel(batch: GenericSuggestion[]): string {
  return isBatchLTLBatch(batch)
    ? `Batch (${batch.length} LTL shipments)`
    : `Batch (${batch.length} lanes)`;
}

function isBatchLTLBatch(batch: GenericSuggestion[]): boolean {
  return batch.every(
    (suggestion) => getBatchLTLShipmentIndex(suggestion) !== undefined
  );
}

function getBatchLTLShipmentIndex(
  suggestion: GenericSuggestion
): Undef<number> {
  if (suggestion.pipeline !== SuggestionPipelines.QuickQuote) {
    return undefined;
  }

  return (
    (suggestion as SuggestedQuoteChange).suggested?.batchLtlShipment?.index ??
    undefined
  );
}

export function getCategoryButtonLabel(pipeline: SuggestionPipelines): string {
  switch (pipeline) {
    case SuggestionPipelines.QuickQuote:
      return 'Quick Quote';
    case SuggestionPipelines.LoadBuilding:
      return 'Load Building';
    default:
      return getSuggestionTitleByPipeline(pipeline);
  }
}
